import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useSession } from '@supabase/auth-helpers-react';
import { useLocation, useNavigate } from 'react-router-dom';
import EmployerCommerceShell from '../../components/employer/EmployerCommerceShell';
import { Button, Card, CardBody, CardHeader, Input, Label, Textarea } from '../../components/ui/primitives';
import {
  createCommerceOrder,
  fetchBillingProfile,
  saveBillingProfile,
  type CommerceBillingProfile,
  type CommerceBillingProfileInput,
  useApi,
} from '../../services/api';
import { parseInvoiceIntent } from '../../services/commerceIntent';

type InvoiceFormState = CommerceBillingProfileInput;

const EMPTY_FORM: InvoiceFormState = {
  legalEntityName: '',
  billingAddress: '',
  accountsPayableEmail: '',
  registrationNumber: '',
  vatNumber: '',
  poNumber: '',
  costCentreNumber: '',
};

const billingAddressToText = (billingAddress: CommerceBillingProfile['billingAddress'] | undefined): string => {
  if (!billingAddress) return '';
  if (typeof billingAddress === 'string') return billingAddress;
  try {
    return JSON.stringify(billingAddress, null, 2);
  } catch {
    return '';
  }
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.message || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const EmployerInvoiceDetailsPage: React.FC = () => {
  const api = useApi();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceIntent = useMemo(() => parseInvoiceIntent(location.search), [location.search]);
  const [formState, setFormState] = useState<InvoiceFormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fallbackCompanyName =
    (session?.user?.app_metadata?.companyName as string | undefined) ||
    (session?.user?.app_metadata?.company as string | undefined) ||
    '';
  const fallbackInvoiceEmail = session?.user?.email || '';

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      setFormState({
        legalEntityName: fallbackCompanyName,
        billingAddress: '',
        accountsPayableEmail: fallbackInvoiceEmail,
        registrationNumber: '',
        vatNumber: '',
        poNumber: '',
        costCentreNumber: '',
      });

      try {
        const profile = await fetchBillingProfile(api);
        if (!mounted) return;
        setFormState({
          legalEntityName: profile?.legalEntityName || fallbackCompanyName,
          billingAddress: billingAddressToText(profile?.billingAddress) || '',
          accountsPayableEmail: profile?.accountsPayableEmail || fallbackInvoiceEmail,
          registrationNumber: profile?.registrationNumber || '',
          vatNumber: profile?.vatNumber || '',
          poNumber: profile?.poNumber || '',
          costCentreNumber: profile?.costCentreNumber || '',
        });
      } catch (error) {
        if (!mounted) return;
        setFormState((current) => ({
          ...current,
          legalEntityName: current.legalEntityName || fallbackCompanyName,
          accountsPayableEmail: current.accountsPayableEmail || fallbackInvoiceEmail,
        }));
        setLoadError(getErrorMessage(error, 'Unable to load saved invoice details right now.'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [api, fallbackCompanyName, fallbackInvoiceEmail]);

  const intentSummary = useMemo(() => {
    if (!invoiceIntent) return null;
    if (invoiceIntent.flow === 'credits') {
      return invoiceIntent.creditQuantity
        ? `Invoice request for ${invoiceIntent.creditQuantity} credits`
        : 'Invoice request for credits';
    }
    return invoiceIntent.roleName
      ? `Invoice request for ${invoiceIntent.roleName}`
      : 'Invoice request for a build';
  }, [invoiceIntent]);

  const updateField = (field: keyof InvoiceFormState, value: string) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setSubmitError(null);

    try {
      await saveBillingProfile(
        {
          legalEntityName: formState.legalEntityName.trim(),
          billingAddress: formState.billingAddress.trim(),
          accountsPayableEmail: formState.accountsPayableEmail.trim(),
          registrationNumber: formState.registrationNumber.trim(),
          vatNumber: formState.vatNumber.trim(),
          poNumber: formState.poNumber.trim(),
          costCentreNumber: formState.costCentreNumber.trim(),
        },
        api
      );

      if (invoiceIntent) {
        if (invoiceIntent.flow === 'build' && !invoiceIntent.roleName) {
          throw new Error('Role name is required before requesting a build invoice.');
        }

        const result = await createCommerceOrder(
          {
            offeringCode: invoiceIntent.offeringCode,
            checkoutMode: 'REQUEST_INVOICE',
            creditQuantity: invoiceIntent.creditQuantity,
            buildRequest:
              invoiceIntent.flow === 'build'
                ? {
                    roleName: invoiceIntent.roleName || '',
                    notes: invoiceIntent.notes,
                  }
                : undefined,
          },
          api
        );

        toast.success('Invoice request sent.');
        navigate(`/billing/order/${result.orderId}`);
        return;
      }

      toast.success('Invoice details saved.');
      navigate('/employer/billing');
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to save invoice details right now.');
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <EmployerCommerceShell
      activeSection="billing"
      title="Invoice details"
      description="We only need a few details to send your invoice."
      actions={
        <Button variant="secondary" onClick={() => navigate('/employer/billing')}>
          Back to billing
        </Button>
      }
    >
      {intentSummary ? (
        <Card className="border-[#dbe4e1] bg-[#fcfffe]">
          <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Current request</p>
          <p className="mt-2 text-[16px] font-semibold text-[#0f172a]">{intentSummary}</p>
        </Card>
      ) : null}

      {loadError ? (
        <Card className="border-[#fde68a] bg-[#fffbeb]">
          <p className="text-[14px] font-medium text-[#92400e]">{loadError}</p>
        </Card>
      ) : null}

      <Card className="border-[#dbe4e1]">
        <CardHeader>
          <h2 className="font-display text-[24px] font-bold text-[#0f172a]">Where should we send the invoice?</h2>
          <p className="mt-1 text-[14px] text-[#64748b]">
            These details are saved for next time, so you should only need to do this once.
          </p>
        </CardHeader>
        <CardBody>
          {loading ? <p className="text-[14px] text-[#64748b]">Loading saved details...</p> : null}

          {!loading ? (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <Label htmlFor="companyName">Company name</Label>
                <Input
                  id="companyName"
                  value={formState.legalEntityName}
                  onChange={(event) => updateField('legalEntityName', event.target.value)}
                  placeholder="Your company name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="invoiceEmail">Email for invoice</Label>
                <Input
                  id="invoiceEmail"
                  type="email"
                  value={formState.accountsPayableEmail}
                  onChange={(event) => updateField('accountsPayableEmail', event.target.value)}
                  placeholder="finance@company.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="billingAddress">Billing address</Label>
                <Textarea
                  id="billingAddress"
                  value={formState.billingAddress}
                  onChange={(event) => updateField('billingAddress', event.target.value)}
                  placeholder="Street, suburb, city, postcode, country"
                  rows={5}
                  required
                />
              </div>

              <details className="rounded-[14px] border border-[#e2e8f0] bg-[#fcfffe] px-4 py-4">
                <summary className="cursor-pointer text-[14px] font-semibold text-[#0f172a]">
                  Add optional finance references
                </summary>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="registrationNumber">Registration number</Label>
                    <Input
                      id="registrationNumber"
                      value={formState.registrationNumber}
                      onChange={(event) => updateField('registrationNumber', event.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="vatNumber">VAT number</Label>
                    <Input
                      id="vatNumber"
                      value={formState.vatNumber}
                      onChange={(event) => updateField('vatNumber', event.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="poNumber">Purchase order number</Label>
                    <Input
                      id="poNumber"
                      value={formState.poNumber}
                      onChange={(event) => updateField('poNumber', event.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="costCentreNumber">Cost centre</Label>
                    <Input
                      id="costCentreNumber"
                      value={formState.costCentreNumber}
                      onChange={(event) => updateField('costCentreNumber', event.target.value)}
                    />
                  </div>
                </div>
              </details>

              {submitError ? <p className="text-[13px] font-medium text-[#b91c1c]">{submitError}</p> : null}

              <div className="flex flex-wrap gap-3">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : invoiceIntent ? 'Save and request invoice' : 'Save invoice details'}
                </Button>
                <Button type="button" variant="secondary" onClick={() => navigate('/employer/billing')}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : null}
        </CardBody>
      </Card>
    </EmployerCommerceShell>
  );
};

export default EmployerInvoiceDetailsPage;
